import qapi from "views_shared/app/javascript/api";
import { throwOnApiError } from "views_shared/app/javascript/api/api";

// Context-fed action hook for everything this expert's promoter side represents — the sibling of
// use_expert_employments_api. The provider owns the `alsoRepresents` collection; this only performs the
// writes against it (optimistic update + snapshot rollback), routes failures to the shared generalError
// (never asyncError, which would tear the page down) and reconciles by reloading. It holds no state.
//
// Removal is representation-id based for every entity type. The representations view fans out one row
// per promoter job, so a deduped collection row keeps ALL of its representation ids in
// `representationIds`; removing the entity destroys every one of them. This deliberately does not read
// the experts view's `representatives` array (the PR page's route to the same ids): that array is keyed
// by the PR profile viewing it, which is not the feed this page renders.
//
// `selfProfileId` — the promoter profile behind a self-representing expert — arrives from the provider
// rather than being inferred here: it is the promoter side that owns these representations, and the
// only correct `promoter_profile_id` for a create.
//
// representations.create/destroy resolve the raw { data } / { error } shape (they are shared with the PR
// page's editors, which read it directly), so this caller opts into throw-on-error via throwOnApiError.
const useExpertRepresentationsApi = ({
  alsoRepresents,
  setAlsoRepresents,
  selfProfileId,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  // Re-sync page state from whatever the server actually committed after a failed write — the local
  // rollback restores the pre-write collection, which is only right if the write truly did nothing (a
  // partially-applied fan-out destroy is exactly the case where it isn't). Nonfatal so a refresh that
  // also fails cannot escalate a recoverable, already-surfaced error into the page error boundary.
  const reconcile = async () => {
    try {
      await silentReload({ nonfatal: true });
    } catch {
      // Deliberately empty: the failure is already on generalError.
    }
  };

  // Resolves { saved } so the caller can keep its surface open (and its error alert visible) on failure.
  // Generic across all three promoted-entity types — no expert-specific field or branch anywhere in its
  // body — so the Products/Companies modal bodies call this same function, not a type-specific sibling.
  const addRepresentation = async (promoEntityId) => {
    if (isUpdating) return { saved: false }; // guard overlapping mutations — a snapshot can't survive concurrency
    // No promoter side means no profile to represent anything; the affordance is gated on the same id.
    if (!selfProfileId) return { saved: false };

    setGeneralError(null);
    setIsUpdating(true);
    try {
      // A reload (not an optimistic insert) is simplest here — the new row's display fields come from
      // the representations view, not from what was submitted.
      throwOnApiError(await qapi.representations.create({ promoterProfileId: selfProfileId, promoEntityId }));
      await silentReload();
      return { saved: true };
    } catch (e) {
      setGeneralError(e);
      await reconcile();
      return { saved: false };
    } finally {
      setIsUpdating(false);
    }
  };

  // Takes the display row the section hands back; `row.id` is the promoted entity's id (the section
  // keys rows by entity, since the fan-out means an entity has no single representation id). Resolves
  // whether the entity was removed, which is what closes the section's confirmation.
  const removeRepresentation = async (row) => {
    if (isUpdating) return false;
    setGeneralError(null);
    const snapshot = alsoRepresents;
    const entityId = row.id;

    // Optimistically drop the entity from the provider collection (functional update — no stale closure).
    setAlsoRepresents((current) => current.filter((entry) => entry.promotedEntityId !== entityId));

    const representationIds = snapshot
      .filter((entry) => entry.promotedEntityId === entityId)
      .flatMap((entry) => entry.representationIds || []);

    setIsUpdating(true);
    try {
      // allSettled, not all: an early rejection must not start the reconcile (or release the isUpdating
      // guard) while sibling destroys are still in flight — every one has to land before the collection
      // is re-read. The first failure still surfaces.
      const outcomes = await Promise.allSettled(
        representationIds.map(async (id) => throwOnApiError(await qapi.representations.destroy(id))),
      );
      const failed = outcomes.find((outcome) => outcome.status === "rejected");
      if (failed) throw failed.reason;
      await silentReload();
      return true;
    } catch (e) {
      setAlsoRepresents(snapshot); // roll back the optimistic removal
      setGeneralError(e);
      await reconcile();
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return { addRepresentation, removeRepresentation };
};

export default useExpertRepresentationsApi;
